import React, { useEffect, useState, useCallback } from "react";
import { api, formatApiError } from "@/lib/api";
import { downloadUrl, isOfficeDoc, officeOnlineViewerUrl, humanFileSize } from "@/lib/attachments";
import { X, Download, ExternalLink, FileText, AlertTriangle } from "lucide-react";

/**
 * Full-screen-ish modal that previews an attachment in-app.
 * - Images: <img> with object-fit contain
 * - PDF, text/plain: <iframe src=signedUrl|tokenUrl>
 * - Office docs: <iframe src=officeOnlineViewerUrl(signedUrl)>
 *
 * Esc closes; click on the dim backdrop closes.
 */
export default function AttachmentViewer({ attachment, onClose }) {
    const [signedUrl, setSignedUrl] = useState(null);
    const [error, setError] = useState("");
    const [loading, setLoading] = useState(true);

    const isImage = attachment?.mime?.startsWith("image/");
    const isPdf = attachment?.mime === "application/pdf";
    const isText = attachment?.mime === "text/plain";
    const isOffice = isOfficeDoc(attachment?.mime);

    const closeOnEsc = useCallback((e) => {
        if (e.key === "Escape") onClose?.();
    }, [onClose]);

    useEffect(() => {
        window.addEventListener("keydown", closeOnEsc);
        return () => window.removeEventListener("keydown", closeOnEsc);
    }, [closeOnEsc]);

    useEffect(() => {
        if (!attachment) return;
        let cancelled = false;
        (async () => {
            setLoading(true); setError(""); setSignedUrl(null);
            try {
                if (isOffice) {
                    const { data } = await api.get(`/attachments/${attachment.attachment_id}/signed-url`, { params: { ttl_seconds: 600 } });
                    if (!cancelled) setSignedUrl(data.url);
                } else {
                    // Use direct token URL for image/pdf/text — same domain, no proxy needed
                    if (!cancelled) setSignedUrl(downloadUrl(attachment.attachment_id, { inline: true }));
                }
            } catch (err) {
                if (!cancelled) setError(formatApiError(err, "No pudimos cargar la previsualización"));
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();
        return () => { cancelled = true; };
    }, [attachment, isOffice]);

    if (!attachment) return null;

    const officeIframeSrc = signedUrl && isOffice ? officeOnlineViewerUrl(signedUrl) : null;
    const directDownload = downloadUrl(attachment.attachment_id);

    return (
        <div
            onMouseDown={(e) => { if (e.target === e.currentTarget) onClose?.(); }}
            data-testid="attachment-viewer-modal"
            style={{
                position: "fixed", inset: 0, zIndex: 80,
                background: "rgba(8,8,10,.85)", backdropFilter: "blur(4px)",
                display: "grid", placeItems: "center", padding: 24,
                animation: "fadeIn .15s ease",
            }}
        >
            <div
                style={{
                    width: "min(1280px, 96vw)",
                    height: "min(880px, 92vh)",
                    background: "var(--bg-1)",
                    border: "1px solid var(--border)",
                    borderRadius: 14,
                    boxShadow: "0 40px 80px -20px rgba(0,0,0,.7)",
                    display: "flex", flexDirection: "column",
                    overflow: "hidden",
                    animation: "pop .15s ease",
                }}
            >
                {/* Header */}
                <div style={{
                    display: "flex", alignItems: "center", justifyContent: "space-between",
                    padding: "14px 18px", borderBottom: "1px solid var(--border)",
                    background: "var(--bg-2)",
                    flexShrink: 0, gap: 12, position: "relative", zIndex: 2,
                }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 10, minWidth: 0, flex: "1 1 auto" }}>
                        <FileText size={18} color="var(--accent)" style={{ flexShrink: 0 }} />
                        <div style={{ minWidth: 0, flex: "1 1 auto" }}>
                            <div style={{ fontSize: 14, fontWeight: 600, color: "var(--text)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} data-testid="viewer-filename">{attachment.filename}</div>
                            <div className="mono text-xs text-muted" style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{attachment.mime} · {humanFileSize(attachment.size)}</div>
                        </div>
                    </div>
                    <div className="flex gap-2" style={{ flexShrink: 0, flexWrap: "nowrap", alignItems: "center" }}>
                        {signedUrl && (
                            <a href={isOffice ? officeIframeSrc : signedUrl} target="_blank" rel="noreferrer" className="btn btn-ghost btn-sm" data-testid="viewer-open-newtab" title="Abrir en pestaña nueva" style={{ whiteSpace: "nowrap", background: "var(--bg-1)", borderColor: "var(--border-strong)", color: "var(--text)" }}>
                                <ExternalLink size={13} /> Pestaña nueva
                            </a>
                        )}
                        <a href={directDownload} className="btn btn-primary btn-sm" data-testid="viewer-download" title="Descargar" style={{ whiteSpace: "nowrap" }}>
                            <Download size={13} /> Descargar
                        </a>
                        <button onClick={onClose} className="btn btn-ghost btn-sm" data-testid="viewer-close" title="Cerrar (Esc)" aria-label="Cerrar" style={{ whiteSpace: "nowrap", background: "var(--bg-1)", borderColor: "var(--border-strong)", color: "var(--text)" }}>
                            <X size={14} />
                        </button>
                    </div>
                </div>

                {/* Body */}
                <div style={{ flex: 1, minHeight: 0, position: "relative", background: isImage ? "#000" : "var(--bg-0)" }}>
                    {loading && (
                        <div style={{ position: "absolute", inset: 0, display: "grid", placeItems: "center" }}>
                            <div style={{ textAlign: "center" }}>
                                <span className="loader loader-lg" />
                                <div className="text-muted mt-3">Cargando previsualización…</div>
                            </div>
                        </div>
                    )}

                    {error && !loading && (
                        <div style={{ position: "absolute", inset: 0, display: "grid", placeItems: "center", padding: 20 }}>
                            <div style={{ textAlign: "center", maxWidth: 420 }}>
                                <AlertTriangle size={36} color="#F59E0B" style={{ margin: "0 auto 10px" }} />
                                <div style={{ color: "var(--text)", fontWeight: 600, fontSize: 15, marginBottom: 6 }}>No se pudo cargar la vista previa</div>
                                <div className="text-muted text-sm">{error}</div>
                                <a href={directDownload} className="btn btn-primary mt-4">
                                    <Download size={14} /> Descargar archivo
                                </a>
                            </div>
                        </div>
                    )}

                    {!loading && !error && signedUrl && (
                        <>
                            {isImage && (
                                <img
                                    src={signedUrl}
                                    alt={attachment.filename}
                                    style={{ width: "100%", height: "100%", objectFit: "contain", display: "block" }}
                                    data-testid="viewer-image"
                                />
                            )}
                            {(isPdf || isText) && (
                                <iframe
                                    src={signedUrl}
                                    title={attachment.filename}
                                    style={{ width: "100%", height: "100%", border: 0, background: "#fff" }}
                                    data-testid="viewer-iframe"
                                />
                            )}
                            {isOffice && officeIframeSrc && (
                                <iframe
                                    src={officeIframeSrc}
                                    title={attachment.filename}
                                    style={{ width: "100%", height: "100%", border: 0, background: "#fff" }}
                                    sandbox="allow-scripts allow-same-origin allow-forms allow-popups"
                                    data-testid="viewer-iframe-office"
                                />
                            )}
                        </>
                    )}
                </div>

                {/* Footer hint */}
                <div style={{
                    padding: "8px 18px", borderTop: "1px solid var(--border)",
                    background: "var(--bg-1)", flexShrink: 0,
                    fontSize: 11.5, color: "var(--text-dim)", textAlign: "center",
                }}>
                    Presiona <kbd style={{ background: "var(--bg-2)", border: "1px solid var(--border-strong)", padding: "1px 6px", borderRadius: 4, fontFamily: "var(--font-mono)" }}>Esc</kbd> para cerrar
                    {isOffice && " · El visor de Microsoft puede tardar unos segundos en renderizar"}
                </div>
            </div>
        </div>
    );
}
